import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import type { CloudinaryAsset } from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { getLongTailPPCData } from '@bulknutrients/bulk-shared-lib/lib/sanity/longTailPPCData';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { Button } from '@components/Button';
import { EmblaCarousel } from '@components/Carousels/Carousel';
import { FeaturedBlogsCarousel } from '@components/Carousels/FeaturedBlogsCarousel';
import { Container } from '@components/Container';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { Logo } from '@components/Header/Logo';
import { Menu } from '@components/Header/Menu/Menu';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { Icons } from '@components/Icons/Icons';
import { CustomImage } from '@components/Media/Image';
import { ModalNoPortal as Modal } from '@components/Modal/Modal';
import { NewsletterSubscriptionForm } from '@components/NewsletterSubForm/NewsletterSubForm';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { ProductCardVerticalVariant } from '@components/ProductCard/ProductCard';
import { TooltipWrapper } from '@components/Tooltip/Tooltip';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useScroll } from '@hooks/useScroll';
import { log } from '@lib/logs';
import type { PortableTextBlock } from '@portabletext/types';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { type ReactElement, useContext, useEffect, useState } from 'react';

const ICON_SIZE = '1.75rem';

const CustomHeader = ({
  heroImage,
  handleSignUpEvent,
  handleMenuEvent,
  headerScrollClass,
}: {
  heroImage: CloudinaryAsset;
  handleSignUpEvent: () => void;
  handleMenuEvent: () => void;
  headerScrollClass: string;
}): ReactElement => (
  <BulkErrorBoundary location="Header">
    <header
      id="header"
      className={`${headerScrollClass} full-bleed sticky left-0 right-0 top-0
        z-1 min-h-[99px] w-full bg-white opacity-80 backdrop-blur-[5px]
        transition-all duration-200 ease-in-out dark:bg-black sm:w-[645px] 
        [&.hide]:-translate-y-full [&.show]:translate-y-0
        `}
    >
      <div className="flex h-full w-full flex-row items-center justify-between px-8 py-2">
        <div className="flex flex-col items-center text-[0.8rem] leading-3">
          <TooltipWrapper text="Menu">
            <Button
              onClick={handleMenuEvent}
              aria-label="Menu"
              className="relative w-10 text-xs ring-grey-mid ring-offset-2 hover:scale-110 hover:rounded-full hover:bg-white hover:text-black hover:ring-1 hover:ring-grey-mid dark:hover:bg-grey-dark dark:hover:text-white"
              buttonStyle="tertiary"
              icon={<Icons.Menu size={ICON_SIZE} />}
            />
          </TooltipWrapper>
          Menu
        </div>
        <Logo />
        <div className="flex flex-col items-center text-[0.8rem] leading-3">
          <TooltipWrapper text="Sign Up">
            <Button
              onClick={handleSignUpEvent}
              aria-label="Sign Up"
              className="relative w-10 text-xs ring-grey-mid ring-offset-2 hover:scale-110 hover:rounded-full hover:bg-white hover:text-black hover:ring-1 hover:ring-grey-mid dark:hover:bg-grey-dark dark:hover:text-white"
              buttonStyle="tertiary"
              icon={<Icons.Social.Email size={ICON_SIZE} />}
            />
          </TooltipWrapper>
          Sign Up
        </div>
      </div>
    </header>
    <CustomImage
      image={heroImage}
      width={645}
      priority
      // have to sit image under header this way because
      // header cannot be fixed bc it moves when the modal pops open in web
      // because when the scroll bar is removed, it shifts fixed components
      className="relative top-[-99px]"
    />
  </BulkErrorBoundary>
);

const CustomFooter = (): ReactElement => {
  return (
    <BulkErrorBoundary location="Footer">
      <div className="relative top-[-99px]">
        <HorizontalDivider className="mx-0 my-8" />
        <NewsletterSubscriptionForm source="BN - Blog Footer Sign Up" />
        <HorizontalDivider className="mx-0 my-8" />
        <section
          className="full-bleed 
          m-0 
          min-h-[450px]
          bg-linear-to-b from-orange 
          from-0% 
          via-orange-60 via-40% to-orange to-100% px-2 py-4"
        >
          <h2 className="mx-auto max-w-narrow-page py-4 font-secondary text-5xl font-bold uppercase text-white">
            Popular Posts from the{' '}
            <TextLink
              prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
              href="/blog"
              className="text-inherit decoration-white hover:decoration-white"
            >
              Bulk Blog
            </TextLink>
          </h2>
          <Paragraph className="mx-auto my-0 max-w-narrow-page pb-4 pt-2 text-left text-white">
            With over 700 recipes and articles, the Bulk Nutrients Blog has
            something for everyone! Find a new workout, meet our ambassadors or
            take a deep dive into our products today.
          </Paragraph>
          <FeaturedBlogsCarousel />
        </section>
        <HorizontalDivider className="mx-0 my-8" />
        <div className="full-bleed flex flex-col items-center justify-start gap-4">
          <div
            className="mx-auto mb-5 mt-0 w-auto p-2.5 text-center 
        text-base text-grey-dark dark:text-white micro:w-[clamp(320px,640px,100%)]"
          >
            We acknowledge the Traditional Owners of the land on which our
            organisation operates, the Melukerdee People of the South East
            Nation and pay our respects to Elders past, present, and emerging.
          </div>

          <div
            className="mx-auto mb-5 mt-0 w-auto border-0
        bg-black dark:bg-grey-dark p-2.5 text-center micro:w-[clamp(320px,640px,100%)]"
          >
            <h3 className="m-0 p-0 font-secondary text-4xl uppercase text-white">
              Bulk Nutrients is proudly <br /> Australian owned and operated.
            </h3>
          </div>
          <address
            className="text-center font-primary text-base font-light not-italic 
        text-grey-dark dark:text-white"
          >
            7 Crabtree Road, Grove, Tasmania, 7109.
            <br />
            ABN: 17 158 981 447
          </address>
          <p className="mb-4 flex flex-wrap justify-center gap-x-6 gap-y-2.5 text-center">
            <TextLink href="/bulk-nutrients-terms-conditions">
              Terms &amp; Conditions
            </TextLink>
            <TextLink href="/bulk-nutrients-packaging-sustainability-strategy">
              Sustainability Strategy
            </TextLink>
            <TextLink href="/bulk-nutrients-privacy-policy">
              Privacy Policy
            </TextLink>
            <TextLink href="/payments">Payment Information</TextLink>
            <TextLink href="/sitemap">Sitemap</TextLink>
          </p>
          <div className="pb-10 text-sm text-grey-dark dark:text-white">
            <p className="mb-4 flex flex-wrap justify-center text-center">
              All prices are in Australian dollars (AUD) and include GST unless
              otherwise stated.
              <br />
              All content copyright &copy; Bulk Nutrients 2008 - 2025
            </p>
          </div>
        </div>
      </div>
    </BulkErrorBoundary>
  );
};

export interface Data {
  title: string;
  metaTitle: string;
  metaDescription: string;
  slug: string;
  heroImage: CloudinaryAsset;
  body: PortableTextBlock | PortableTextBlock[];
  purchaseProducts: BaseProduct[];
}

export default function LongTailPPCPage({
  pageData,
}: {
  pageData: Data;
}): ReactElement {
  const [headerScrollClass, setHeaderScrollClass] = useState<string>('show');
  const [showSubscriptionModal, setShowSubscriptionModal] =
    useState<boolean>(false);
  const { setMenuIsOpen } = useContext<UIContextInterface>(UIContext);

  const { setRecentlyViewedPage } = useLocalStorage();

  useEffect(() => {
    setRecentlyViewedPage({
      page: { slug: pageData.slug, title: pageData.title },
      viewedTime: Date.now(),
    });
  }, [pageData, setRecentlyViewedPage]);

  const { y, lastY } = useScroll();

  useEffect(() => {
    let mounted = true;

    const headerHeight = (document.getElementById('header') as HTMLElement)
      .offsetHeight;

    const newClass =
      headerHeight && y > lastY && y > headerHeight ? 'hide' : 'show';

    if (mounted && newClass !== headerScrollClass)
      setHeaderScrollClass(newClass);

    return () => {
      mounted = false;
    };
  }, [y, lastY, headerScrollClass]);

  const handleMenuEvent = () => setMenuIsOpen(true);
  const handleSignUpEvent = () => setShowSubscriptionModal(true);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        #loyaltylion {
          display: none;
        }
      `,
        }}
      />
      <Head>
        <title>{pageData.metaTitle}</title>
        <meta name="title" content={pageData.metaTitle} />
        <meta name="description" content={pageData.metaDescription} />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}${pageData.slug}`}
        />
      </Head>
      <Container as="main" id="main-content" className="flex justify-center">
        <div className="absolute z-0 hidden px-0! blur-xs sm:block">
          <CustomImage
            image={pageData.heroImage}
            width={pageData.heroImage.width}
            priority
          />
          <div
            className={mergeStyles(
              'dark:from-black dark:to-black/40 from-white to-white/40',
              'absolute inset-0 bg-linear-to-t'
            )}
          />
        </div>
        <BulkErrorBoundary location="Menu">
          <Menu />
        </BulkErrorBoundary>
        <div className="w-[645px] min-w-xs px-0!">
          <CustomHeader
            heroImage={pageData.heroImage}
            headerScrollClass={headerScrollClass}
            handleMenuEvent={handleMenuEvent}
            handleSignUpEvent={handleSignUpEvent}
          />
          <section className="relative top-[-99px] bg-white px-4 dark:bg-black">
            <h1 className="mt-0 pt-6">{pageData.title}</h1>
            <PortableTextBlockRenderer content={pageData.body} />
            <div className="light-background full-bleed my-5">
              <EmblaCarousel
                title="Purchase products"
                slides={pageData.purchaseProducts.map((item: BaseProduct) => (
                  <ProductCardVerticalVariant
                    product={item as BaseProduct}
                    key={(item as BaseProduct).entityId}
                    className="w-xs tiny:w-[400px]"
                  />
                ))}
              />
            </div>
          </section>
          <CustomFooter />
        </div>
      </Container>
      <Modal
        show={showSubscriptionModal}
        setShow={setShowSubscriptionModal}
        className="min-w-[300px] max-w-[600px] p-0"
      >
        <NewsletterSubscriptionForm
          source="BN - Long Tail PPC Sign Up"
          className="bg-white text-black dark:bg-black dark:text-white"
        />
      </Modal>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = [];

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    const pageData = await getLongTailPPCData(
      params?.slug?.toString() as string
    );

    if (!pageData) {
      // try redirect
      const redirect = await getRedirect(
        `/info/${params?.slug?.toString().split(',').join('/')}` as string
      );

      if (redirect) {
        return {
          redirect: {
            destination: redirect.newUrl,
            permanent: redirect.permanent,
          },
        };
      }

      // fallback failed
      return {
        notFound: true,
      };
    }

    return {
      props: {
        pageData,
      },
    };
  } catch (error) {
    log({ error });
    return {
      props: {
        pageData: {},
      },
    };
  }
};
